import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	CardBody,
	Input,
	Modal,
	Page,
	Select,
	Table,
	type TableColumn,
	Tabs,
	useToast,
} from "@theg1group/ui";
import { ExternalLink } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { GraphView } from "../components/GraphView.tsx";
import { RiskBadge } from "../components/RiskBadge.tsx";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtAmount, fmtDate, fmtNum, fmtUsd, severityTone } from "../lib/format.ts";
import type {
	AddressDetailData,
	CaseRow,
	CounterpartyRow,
	Rows,
	TransferRow,
	TransfersPage,
} from "../lib/types.ts";

const DIR_OPTIONS = [
	{ value: "", label: "In + out" },
	{ value: "in", label: "Inbound" },
	{ value: "out", label: "Outbound" },
];

export function AddressDetail() {
	const { id = "" } = useParams();
	const qc = useQueryClient();
	const toast = useToast();
	const navigate = useNavigate();
	const [tab, setTab] = useState("transfers");
	const [showCase, setShowCase] = useState(false);
	const [showMerge, setShowMerge] = useState(false);
	const [mergeOther, setMergeOther] = useState("");

	const detail = useQuery({
		queryKey: ["address", id],
		queryFn: () => api.get<AddressDetailData>(`/api/addresses/${id}`),
		enabled: !!id,
	});

	const watch = useMutation({
		mutationFn: (enabled: boolean) => api.post(`/api/addresses/${id}/watch`, { enabled }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["address", id] }),
		onError: (e) => toast.addToast("error", String(e)),
	});
	const refetch = useMutation({
		mutationFn: () => api.post<{ jobId: string }>(`/api/addresses/${id}/refetch`),
		onSuccess: (r) => {
			toast.addToast("success", `Refetch queued (${r.jobId.slice(0, 8)}…)`);
			qc.invalidateQueries({ queryKey: ["traces"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const merge = useMutation({
		mutationFn: () => api.post("/api/clusters/merge", { addressIds: [id, mergeOther.trim()] }),
		onSuccess: () => {
			toast.addToast("success", "Merge queued — cluster re-derives shortly");
			setShowMerge(false);
			setMergeOther("");
			window.setTimeout(() => qc.invalidateQueries({ queryKey: ["address", id] }), 5_000);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const d = detail.data;
	const a = d?.address;

	if (detail.isLoading) return <Page title="Address">Loading…</Page>;
	if (!d || !a) return <Page title="Address">Not found.</Page>;

	return (
		<Page
			title={
				<span className="inline-flex items-center gap-2 flex-wrap">
					<ChainBadge chain={a.chainId} />
					<AddressLink address={a.address} display={a.display} full className="text-base" />
					{a.explorer && (
						<a
							href={a.explorer}
							target="_blank"
							rel="noreferrer"
							className="text-muted hover:text-content"
							title="Explorer"
						>
							<ExternalLink size={14} />
						</a>
					)}
				</span>
			}
			description={
				<span className="inline-flex items-center gap-2 flex-wrap">
					{a.kind && <Badge>{a.kind}</Badge>}
					<RiskBadge score={a.riskScore} />
					{a.isSupernode && <Badge variant="info">supernode</Badge>}
					{d.cluster && (
						<Link
							to={`/clusters/${d.cluster.id}`}
							className="hover:opacity-80"
							title={`min confidence ${Number(d.cluster.minConfidence).toFixed(2)}${d.cluster.rule ? ` · via ${d.cluster.rule}` : ""}`}
						>
							<Badge variant="info">Cluster · {d.cluster.size} addrs</Badge>
						</Link>
					)}
					<span className="text-muted">
						in {fmtNum(a.inDegree)} · out {fmtNum(a.outDegree)} · {fmtNum(a.txCount)} tx
					</span>
				</span>
			}
			actions={
				<div className="flex flex-wrap gap-2">
					<Button
						variant={d.watch?.enabled ? "secondary" : "outline"}
						loading={watch.isPending}
						onClick={() => watch.mutate(!d.watch?.enabled)}
					>
						{d.watch?.enabled ? "Watching ✓" : "Watch"}
					</Button>
					<Button variant="outline" loading={refetch.isPending} onClick={() => refetch.mutate()}>
						Refetch
					</Button>
					<Button
						variant="outline"
						onClick={() => navigate(`/graph?focus=${encodeURIComponent(`address:${a.id}`)}`)}
					>
						Focus in graph
					</Button>
					<Button variant="outline" onClick={() => setShowMerge(true)}>
						Merge into cluster…
					</Button>
					<Button variant="primary" onClick={() => setShowCase(true)}>
						Add to case
					</Button>
				</div>
			}
		>
			<div
				className="grid gap-3.5 mb-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
			>
				<Panel title="Labels">
					<div className="flex flex-wrap gap-1">
						{d.labels.map((l) => (
							<span
								key={l.id}
								title={`${l.sourceId}${l.confidence !== null ? ` · conf ${l.confidence}` : ""}`}
							>
								<Badge variant={l.severity ? severityTone(l.severity) : "warning"}>
									{l.category}
									{l.entity ? ` · ${l.entity}` : ""}
									{l.stopExpansion ? " ⛔" : ""}
								</Badge>
							</span>
						))}
						{!d.labels.length && <span className="text-xs text-muted">No labels.</span>}
					</div>
				</Panel>
				<Panel title="Partners">
					{d.partners.map((p) => (
						<div key={p.partnerId} className="text-sm flex items-center gap-2 mb-1">
							<Link to={`/partners/${p.partnerId}`} className="text-primary hover:underline">
								{p.name}
							</Link>
							<span className="text-[11px] text-muted">{p.status}</span>
						</div>
					))}
					{!d.partners.length && <span className="text-xs text-muted">Not a partner wallet.</span>}
				</Panel>
				<Panel title="Activity window">
					<KV k="First seen" v={fmtDate(a.firstSeen)} />
					<KV k="Last seen" v={fmtDate(a.lastSeen)} />
					<KV
						k="Fetched"
						v={`${fmtDate(d.window.fetchedFrom, false)} → ${fmtDate(d.window.fetchedTo, false)}`}
					/>
					<KV k="Last fetch" v={fmtAgo(d.window.lastFetchedAt)} />
					{d.watch && (
						<KV
							k="Watch"
							v={
								d.watch.enabled
									? `on · polled ${fmtAgo(d.watch.lastPolledAt)} · next ${fmtDate(d.watch.nextPollAt)}`
									: "off"
							}
						/>
					)}
				</Panel>
			</div>

			<Tabs
				activeTab={tab}
				onTabChange={setTab}
				tabs={[
					{ id: "transfers", label: "Transfers", content: <TransfersTab id={id} /> },
					{ id: "counterparties", label: "Counterparties", content: <CounterpartiesTab id={id} /> },
					{
						id: "graph",
						label: "Graph",
						content: (
							<GraphView
								focus={`address:${id}`}
								depth={1}
								minUsd={0}
								height="560px"
								onFocus={(f) => navigate(`/graph?focus=${encodeURIComponent(f)}`)}
							/>
						),
					},
				]}
			/>

			<Modal
				open={showMerge}
				onClose={() => setShowMerge(false)}
				title="Merge into cluster"
				footer={
					<div className="flex justify-end gap-2">
						<Button variant="ghost" onClick={() => setShowMerge(false)}>
							Cancel
						</Button>
						<Button
							variant="primary"
							disabled={!mergeOther.trim()}
							loading={merge.isPending}
							onClick={() => merge.mutate()}
						>
							Merge
						</Button>
					</div>
				}
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm text-content">
						Writes manual clustering evidence (confidence 1.0) between{" "}
						<span className="font-mono text-xs break-all">{a.display}</span> and the address below
						(same chain), then queues a re-derive.
					</p>
					<Input
						label="Other address id"
						value={mergeOther}
						onChange={(e) => setMergeOther(e.target.value)}
						placeholder="Address id (uuid)"
					/>
				</div>
			</Modal>

			<AddToCaseModal
				open={showCase}
				onClose={() => setShowCase(false)}
				itemType="address"
				itemId={a.id}
				label={a.display}
			/>
		</Page>
	);
}

function TransfersTab({ id }: { id: string }) {
	const [dir, setDir] = useState("");
	const [minUsd, setMinUsd] = useState("");
	const q = useInfiniteQuery({
		queryKey: ["transfers", id, dir, minUsd],
		queryFn: ({ pageParam }) => {
			const p = new URLSearchParams({ limit: "50" });
			if (dir) p.set("dir", dir);
			if (minUsd) p.set("minUsd", minUsd);
			if (pageParam) p.set("cursor", pageParam);
			return api.get<TransfersPage>(`/api/addresses/${id}/transfers?${p.toString()}`);
		},
		initialPageParam: "",
		getNextPageParam: (last) => last.nextCursor ?? undefined,
	});
	const rows = q.data?.pages.flatMap((p) => p.rows) ?? [];

	const cols: TableColumn<TransferRow>[] = [
		{
			id: "ts",
			header: "Time",
			accessor: (r) => <span title={fmtDate(r.ts)}>{fmtAgo(r.ts)}</span>,
		},
		{
			id: "dir",
			header: "",
			accessor: (r) =>
				r.toAddressId === id ? (
					<Badge variant="success" size="sm">
						in
					</Badge>
				) : (
					<Badge variant="warning" size="sm">
						out
					</Badge>
				),
		},
		{
			id: "from",
			header: "From",
			accessor: (r) => (
				<div>
					<AddressLink id={r.fromAddressId} address={r.fromAddress} display={r.fromDisplay} />
					{r.fromPartner && <div className="text-[11px] text-primary">{r.fromPartner}</div>}
				</div>
			),
		},
		{
			id: "to",
			header: "To",
			accessor: (r) => (
				<div>
					<AddressLink id={r.toAddressId} address={r.toAddress} display={r.toDisplay} />
					{r.toPartner && <div className="text-[11px] text-primary">{r.toPartner}</div>}
				</div>
			),
		},
		{ id: "amount", header: "Amount", accessor: (r) => fmtAmount(r.amount, r.symbol) },
		{ id: "usd", header: "USD", accessor: (r) => fmtUsd(r.amountUsd) },
		{ id: "kind", header: "Kind", accessor: (r) => r.kind ?? "—" },
		{
			id: "tx",
			header: "Tx",
			accessor: (r) => (
				<span className="inline-flex items-center gap-1 font-mono text-xs">
					{r.txHash.slice(0, 10)}…
					{r.explorer && (
						<a
							href={r.explorer}
							target="_blank"
							rel="noreferrer"
							className="text-muted hover:text-content"
						>
							<ExternalLink size={12} />
						</a>
					)}
				</span>
			),
		},
	];

	return (
		<Card>
			<CardBody>
				<div className="mb-3 flex gap-2">
					<div className="w-40">
						<Select
							value={dir}
							onChange={(e) => setDir(e.target.value)}
							options={DIR_OPTIONS}
							size="sm"
						/>
					</div>
					<div className="w-40">
						<Input
							type="number"
							min={0}
							value={minUsd}
							onChange={(e) => setMinUsd(e.target.value)}
							placeholder="Min USD"
							size="sm"
						/>
					</div>
				</div>
				<div className="overflow-x-auto">
					<Table
						columns={cols}
						data={rows}
						keyExtractor={(r) => r.id}
						loading={q.isLoading}
						emptyMessage="No transfers in window."
					/>
				</div>
				{q.hasNextPage && (
					<div className="mt-3">
						<Button
							variant="outline"
							size="sm"
							loading={q.isFetchingNextPage}
							onClick={() => q.fetchNextPage()}
						>
							Load more
						</Button>
					</div>
				)}
			</CardBody>
		</Card>
	);
}

function CounterpartiesTab({ id }: { id: string }) {
	const [dir, setDir] = useState("");
	const [minUsd, setMinUsd] = useState("");
	const q = useQuery({
		queryKey: ["counterparties", id, dir, minUsd],
		queryFn: () => {
			const p = new URLSearchParams();
			if (dir) p.set("dir", dir);
			if (minUsd) p.set("minUsd", minUsd);
			return api.get<Rows<CounterpartyRow>>(`/api/addresses/${id}/counterparties?${p.toString()}`);
		},
	});
	const rows = q.data?.rows ?? [];
	const max = Math.max(...rows.map((r) => r.usdSum), 1);

	const cols: TableColumn<CounterpartyRow>[] = [
		{
			id: "dir",
			header: "",
			accessor: (r) => (
				<Badge variant={r.direction === "in" ? "success" : "warning"} size="sm">
					{r.direction}
				</Badge>
			),
		},
		{ id: "chain", header: "Chain", accessor: (r) => <ChainBadge chain={r.chain} /> },
		{
			id: "address",
			header: "Counterparty",
			accessor: (r) => (
				<div>
					<AddressLink id={r.addressId} address={r.address} display={r.display} />
					{r.partners.length > 0 && (
						<div className="text-[11px] text-primary">{r.partners.join(", ")}</div>
					)}
					{r.labels.length > 0 && (
						<div className="flex flex-wrap gap-1 mt-0.5">
							{r.labels.map((l) => (
								<Badge key={l} variant="warning" size="sm">
									{l}
								</Badge>
							))}
						</div>
					)}
				</div>
			),
		},
		{ id: "tx", header: "Tx", accessor: (r) => fmtNum(r.txCount) },
		{
			id: "usd",
			header: "USD",
			accessor: (r) => (
				<div className="min-w-[140px]">
					<div className="text-xs text-content">{fmtUsd(r.usdSum)}</div>
					<div className="h-1.5 rounded-pill bg-surface-2 overflow-hidden">
						<div
							className={`h-full ${r.direction === "in" ? "bg-success" : "bg-warning"}`}
							style={{ width: `${(r.usdSum / max) * 100}%` }}
						/>
					</div>
				</div>
			),
		},
		{ id: "first", header: "First", accessor: (r) => fmtDate(r.firstTs, false) },
		{ id: "last", header: "Last", accessor: (r) => fmtAgo(r.lastTs) },
		{
			id: "flags",
			header: "",
			accessor: (r) =>
				r.isSupernode ? (
					<Badge variant="info" size="sm">
						supernode
					</Badge>
				) : null,
		},
	];

	return (
		<Card>
			<CardBody>
				<div className="mb-3 flex gap-2">
					<div className="w-40">
						<Select
							value={dir}
							onChange={(e) => setDir(e.target.value)}
							options={DIR_OPTIONS}
							size="sm"
						/>
					</div>
					<div className="w-40">
						<Input
							type="number"
							min={0}
							value={minUsd}
							onChange={(e) => setMinUsd(e.target.value)}
							placeholder="Min USD"
							size="sm"
						/>
					</div>
				</div>
				<div className="overflow-x-auto">
					<Table
						columns={cols}
						data={rows}
						keyExtractor={(r) => `${r.direction}:${r.addressId}`}
						loading={q.isLoading}
						emptyMessage="No counterparties."
					/>
				</div>
			</CardBody>
		</Card>
	);
}

/** Pick an existing case and attach an item. Shared by Address + Partner pages. */
export function AddToCaseModal({
	open,
	onClose,
	itemType,
	itemId,
	label,
}: {
	open: boolean;
	onClose: () => void;
	itemType: string;
	itemId: string;
	label?: string;
}) {
	const toast = useToast();
	const qc = useQueryClient();
	const [caseId, setCaseId] = useState("");
	const [note, setNote] = useState("");
	const cases = useQuery({
		queryKey: ["cases", ""],
		queryFn: () => api.get<Rows<CaseRow>>("/api/cases"),
		enabled: open,
	});
	const add = useMutation({
		mutationFn: () =>
			api.post(`/api/cases/${caseId}/items`, { itemType, itemId, note: note || undefined }),
		onSuccess: () => {
			toast.addToast("success", "Added to case");
			qc.invalidateQueries({ queryKey: ["case", caseId] });
			setNote("");
			onClose();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	return (
		<Modal
			open={open}
			onClose={onClose}
			title="Add to case"
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!caseId}
						loading={add.isPending}
						onClick={() => add.mutate()}
					>
						Add
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				{label && <div className="font-mono text-xs text-content break-all">{label}</div>}
				<Select
					label="Case"
					value={caseId}
					onChange={(e) => setCaseId(e.target.value)}
					options={(cases.data?.rows ?? []).map((c) => ({ value: c.id, label: c.name }))}
					placeholder={cases.isLoading ? "Loading…" : "Select case"}
				/>
				<Input
					label="Note"
					value={note}
					onChange={(e) => setNote(e.target.value)}
					placeholder="Optional"
				/>
				{!cases.isLoading && !cases.data?.rows.length && (
					<div className="text-xs text-muted">
						No cases yet —{" "}
						<Link to="/cases" className="text-primary hover:underline">
							create one
						</Link>
						.
					</div>
				)}
			</div>
		</Modal>
	);
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
	return (
		<Card>
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-2">{title}</h2>
				{children}
			</CardBody>
		</Card>
	);
}

function KV({ k, v }: { k: string; v: string }) {
	return (
		<div className="flex justify-between gap-2 text-xs mb-1">
			<span className="text-muted">{k}</span>
			<span className="text-content text-right">{v}</span>
		</div>
	);
}
